/**
 * Ô chọn người ghi công (CEO 2a, 30/10/2026): chỉ người đang ở Phòng Kinh doanh
 * trong tháng, đúng loại việc (sale / CSKH). Bạn của tiểu đội đơn đưa lên đầu
 * thành nút bấm một chạm; người khác tìm theo tên, xếp theo tiểu đội. Máy chủ vẫn
 * tự kiểm luật — ô này chỉ để chọn nhanh và khó chọn nhầm.
 */
import { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { removeAccents } from "@/lib/orderUtils";
import { Input } from "@/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/popover";
import { StaffPicker } from "@/components/StaffPicker";
import { useSalesOrg } from "../api";
import { type PickKind, type PickPerson, pickGroups } from "../helpers";
import { PersonMark, SquadMark } from "./marks";

function fold(s: string): string {
  return removeAccents(s).toLowerCase().trim();
}

type QuickProps = { people: PickPerson[]; value: string | null; onChange: (id: string | null) => void; label: string };

/** Bạn cùng tiểu đội của đơn: nút bấm một chạm; bấm lại nút đang chọn là bỏ chọn. */
function QuickChips({ people, value, onChange, label }: QuickProps) {
  return (
    <div role="group" aria-label={`${label} — bạn cùng tiểu đội của đơn`} className="flex flex-wrap gap-2">
      {people.map((p) => {
        const on = p.staffId === value;
        return (
          <button
            key={p.staffId}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? null : p.staffId)}
            className={cn(
              "inline-flex min-h-11 items-center rounded-full border py-1 pl-1 pr-3 text-sm",
              on ? "border-primary bg-primary/10 font-semibold" : "border-border bg-surface hover:bg-surface-hover",
            )}
          >
            <PersonMark staffId={p.staffId} name={p.name} size={28} title={false} />
          </button>
        );
      })}
    </div>
  );
}

type Props = {
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  kind: PickKind;
  /** YYYY-MM; vắng = tháng hôm nay. */
  month?: string | undefined;
  /** Tiểu đội của đơn — bạn trong đó thành nút bấm một chạm. */
  preferSquadIds?: readonly string[] | undefined;
};

export function SalesPersonPicker({ value, onChange, placeholder = "Chọn người", kind, month, preferSquadIds }: Props) {
  const org = useSalesOrg();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const prefer = useMemo(() => preferSquadIds ?? [], [preferSquadIds]);
  const theMonth = month || org.data?.today.slice(0, 7) || "";
  const groups = useMemo(
    () => (org.data ? pickGroups(org.data, theMonth, kind, prefer) : []),
    [org.data, theMonth, kind, prefer],
  );

  // Không đọc được cơ cấu Phòng KD ⇒ lùi về ô chọn nhân sự chung, vẫn chọn được.
  if (org.isError) return <StaffPicker value={value} onChange={(id) => onChange(id)} placeholder={placeholder} />;
  if (!org.data) return <p className="text-sm text-muted">Đang tải danh sách Phòng Kinh doanh…</p>;

  const quick = groups.filter((g) => prefer.includes(g.squadId)).flatMap((g) => g.people);
  const quickIds = new Set(quick.map((p) => p.staffId));
  const selectedName =
    groups.flatMap((g) => g.people).find((p) => p.staffId === value)?.name ??
    org.data.members.find((m) => m.staffId === value)?.staffName ??
    value ??
    "";
  const needle = fold(q);
  const shown = groups
    .map((g) => ({ ...g, people: needle ? g.people.filter((p) => fold(p.name).includes(needle)) : g.people }))
    .filter((g) => g.people.length > 0);
  const pick = (id: string) => {
    onChange(id);
    setOpen(false);
    setQ("");
  };

  return (
    <div className="space-y-2">
      {quick.length > 0 ? <QuickChips people={quick} value={value} onChange={onChange} label={placeholder} /> : null}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={placeholder}
            className="flex min-h-11 w-full min-w-0 items-center justify-between gap-2 rounded-md border border-border bg-surface px-3 text-left text-sm"
          >
            {value && !quickIds.has(value) ? (
              <PersonMark staffId={value} name={selectedName} title={false} />
            ) : (
              <span className="text-muted">{quick.length > 0 ? "Tìm bạn ở tiểu đội khác…" : placeholder}</span>
            )}
            <ChevronDown className="size-4 shrink-0 text-muted" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(92vw,24rem)] p-0">
          <div className="border-b border-border p-2">
            <Input
              autoFocus
              aria-label="Tìm theo tên"
              placeholder="Gõ tên để tìm"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="max-h-80 overflow-y-auto p-1">
            {shown.length === 0 ? (
              <p className="p-3 text-sm text-muted">
                {needle ? "Không có ai tên này trong Phòng Kinh doanh tháng này." : "Tháng này Phòng Kinh doanh chưa có ai phù hợp."}
              </p>
            ) : (
              shown.map((g) => (
                <div key={g.squadId} role="group" aria-label={`Tiểu đội ${g.name}`}>
                  <p className="px-2 pb-1 pt-2 text-xs font-semibold text-muted">
                    <SquadMark squadId={g.squadId} name={g.name} size={18} />
                  </p>
                  {g.people.map((p) => (
                    <button
                      key={p.staffId}
                      type="button"
                      onClick={() => pick(p.staffId)}
                      className={cn(
                        "flex min-h-11 w-full items-center rounded px-2 text-left text-sm hover:bg-surface-hover",
                        p.staffId === value && "bg-primary/10 font-semibold",
                      )}
                    >
                      <PersonMark staffId={p.staffId} name={p.name} size={28} title={false} />
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
